import { Form, useFormikContext } from 'formik';
import Link from 'next/link';
import { useEffect } from 'react';
import styled, { css } from 'styled-components';

import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { Button } from 'shared/components/Button';
import { FormikInput } from 'shared/components/FormikInput';
import { formatGiftCardCode } from 'shared/utils/formatGiftCardCode';
import { variableFont, variableFontRegular } from 'styles/theme';
import { t } from 'localization/utils/t';

const TP = 'giftCards.components.checkBalance.CheckBalanceForm';

interface ICheckBalanceForm {
  email: string;
  code: string;
}

interface ICheckBalanceFormProps {
  balance?: number;
  errorMessage?: string;
}

export const CheckBalanceForm = ({ balance, errorMessage }: ICheckBalanceFormProps) => {
  const { dirty, isSubmitting, isValid, setFieldValue, values } =
    useFormikContext<ICheckBalanceForm>();

  useEffect(() => {
    void setFieldValue('code', formatGiftCardCode(values.code));
  }, [values.code]);

  return (
    <CheckBalanceForm.Wrapper>
      <Form>
        <CheckBalanceForm.Header>
          <CheckBalanceForm.Title>
            {t(`${TP}.checkGiftCardBalance`, 'Check GOAT Gift Card Balance')}
          </CheckBalanceForm.Title>
          {balance >= 0 && (
            <CheckBalanceForm.Title>
              {formatMoneyObject({ amount: balance, currency: 'USD' })}
            </CheckBalanceForm.Title>
          )}
        </CheckBalanceForm.Header>
        <FormikInput
          label={t(`${TP}.email`, 'Email')}
          name="email"
          data-qa="gift_card_balance_email_input"
        />
        <FormikInput
          label={t(`${TP}.code`, '16-Digit Code')}
          name="code"
          id="code"
          data-qa="gift_card_balance_email_input"
        />
        {errorMessage && <CheckBalanceForm.Error>{errorMessage}</CheckBalanceForm.Error>}
        <CheckBalanceForm.Button
          title={t(`${TP}.checkBalance`, 'Check Balance')}
          disabled={isSubmitting || !isValid || !dirty}
          type="submit"
          data-qa="gift-cards-check-balance-button"
        />
        <CheckBalanceForm.Help>
          {t(`${TP}.needHelp`, 'Need Help?')}
          <CheckBalanceForm.Link>
            <Link
              href="https://support.goat.com/hc/en-us/categories/11113687728013-Gift-Cards"
              passHref
              prefetch={false}
              target="_blank"
              rel="noreferrer noopener"
            >
              {t(`${TP}.support`, 'Support')}
            </Link>
          </CheckBalanceForm.Link>
        </CheckBalanceForm.Help>
      </Form>
    </CheckBalanceForm.Wrapper>
  );
};

CheckBalanceForm.Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding-top: 16px;
    padding-bottom: 16px;
    width: 100%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 16px;
    width: 347px;
  }
`;

CheckBalanceForm.Header = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
`;

CheckBalanceForm.Title = styled.h1`
  ${variableFont({ size: '11.5px', height: '14px' })};
`;

CheckBalanceForm.Error = styled.div`
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
  margin-top: -8px;
  ${({ theme }) => css`
    color: ${theme.colors.red};
  `}
`;

CheckBalanceForm.Button = styled(Button)`
  margin: 32px 0px 32px 0px;
`;

CheckBalanceForm.Help = styled.div`
  display: flex;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  height: 14px;
`;

CheckBalanceForm.Link = styled.div`
  margin-left: 3px;
  cursor: pointer;
  text-decoration: underline;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  > a {
    ${variableFontRegular({ size: '11.5px', height: '14px' })};
  }

  > button {
    padding: 0;
  }
`;
